"use client";

import type React from "react";
import { useEffect, useRef } from "react";

interface BoundingBox {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
  label?: string;
}

interface VideoSectionProps {
  imageSrc: string;
  boxes: BoundingBox[];
  studioName: string;
  projectTitle: string;
  className?: string;
  priority?: boolean;
}

const VideoSection: React.FC<VideoSectionProps> = ({
  imageSrc,
  boxes,
  studioName,
  projectTitle,
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const container = containerRef.current;
    if (!ctx || !canvas || !container) return;

    let isMounted = true;
    const imgElement = new window.Image();

    const updateCanvas = () => {
      if (!isMounted || !ctx || !canvas || !container) return;
      
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Use device pixel ratio for sharp rendering
      const dpr = window.devicePixelRatio || 1;
      canvas.width = containerWidth * dpr;
      canvas.height = containerHeight * dpr;
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;

      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, containerWidth, containerHeight);

      // Calculate object-fit: cover dimensions
      const imgAspect = imgElement.width / imgElement.height;
      const containerAspect = containerWidth / containerHeight;
      
      let drawWidth, drawHeight, offsetX, offsetY;
      
      if (imgAspect > containerAspect) {
        // Image is wider than container - fit to height
        drawHeight = containerHeight;
        drawWidth = containerHeight * imgAspect;
        offsetX = (containerWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        // Image is taller than container - fit to width
        drawWidth = containerWidth;
        drawHeight = containerWidth / imgAspect;
        offsetX = 0;
        offsetY = (containerHeight - drawHeight) / 2;
      }
      
      // Draw image with object-fit: cover behavior
      ctx.drawImage(imgElement, offsetX, offsetY, drawWidth, drawHeight);

      // Draw bounding boxes
      const boxColor = "rgb(34, 197, 94)";
      const transparentColor = "rgba(34, 197, 94, 0.25)";

      boxes.forEach((box) => {
        // Transform box coordinates to account for object-fit: cover offset
        const x = offsetX + (box.x_min * drawWidth);
        const y = offsetY + (box.y_min * drawHeight);
        const width = (box.x_max - box.x_min) * drawWidth;
        const height = (box.y_max - box.y_min) * drawHeight;

        ctx.fillStyle = transparentColor;
        ctx.fillRect(x, y, width, height);

        ctx.strokeStyle = boxColor;
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, width, height);

        // Draw label if present
        if (box.label) {
          ctx.font = "bold 14px system-ui, -apple-system, sans-serif";
          const textMetrics = ctx.measureText(box.label);
          const textWidth = textMetrics.width;
          const textHeight = 14;
          const padding = 6;
          
          // Position label above the box
          const labelX = x;
          const labelY = Math.max(y - 4, textHeight + padding);
          
          // Draw label background
          ctx.fillStyle = boxColor;
          ctx.fillRect(labelX, labelY - textHeight - padding, textWidth + padding * 2, textHeight + padding * 2);
          
          // Draw label text
          ctx.fillStyle = "white";
          ctx.fillText(box.label, labelX + padding, labelY - padding);
        }
      });
    };

    imgElement.onload = () => {
      if (!isMounted) return;
      updateCanvas();
    };

    imgElement.src = imageSrc;

    const resizeObserver = new ResizeObserver(() => {
      if (imgElement.complete) {
        updateCanvas();
      }
    });

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      isMounted = false;
      resizeObserver.disconnect();
    };
  }, [imageSrc, boxes]);

  return (
    <div
      ref={containerRef}
      className={`group relative w-full overflow-hidden rounded-lg ${className}`}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />

      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

      <div className="absolute inset-0 bottom-6 flex items-end">
        <div className="px-4 text-left text-white sm:px-5 md:px-6 lg:px-8">
          <p className="mb-1 text-xs font-medium uppercase tracking-wider opacity-80 sm:text-sm md:text-base">
            {studioName}
          </p>
          <h2 className="max-w-md text-2xl font-light tracking-tight sm:text-3xl md:text-4xl lg:max-w-lg" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' }}>
            {projectTitle}
          </h2>
        </div>
      </div>
    </div>
  );
};

const Demos: React.FC = () => {
  const demoSections = [
    {
      imageSrc: "/images/solutions/identify-defects/scratches_example_lg.jpeg",
      boxes: [
        {
          x_min: 0.33366203808829564,
          y_min: 0.3435312721627692,
          x_max: 0.6673154790182537,
          y_max: 0.6574462449437801,
          label: "scratches"
        }
      ],
      studioName: "Use Case",
      projectTitle: "Damage Detection",
    },
    {
      imageSrc: "/images/solutions/robot-vision/robot_dishes_example.jpg",
      boxes: [
        {
          x_min: 0.4222182512910134,
          y_min: 0.5794431174211956,
          x_max: 0.7038814554538547,
          y_max: 0.9063828845338386,
          label: "dirty dishes"
        }
      ],
      studioName: "Use Case",
      projectTitle: "Robotics",
    },
    {
      imageSrc: "/images/solutions/computer-use/leather_bag_example.jpeg",
      boxes: [
        {
          x_min: 0.5197559427556355,
          y_min: 0.4612354409941971,
          x_max: 0.9484747512815102,
          y_max: 0.5221467681944637,
          label: "add to cart button"
        }
      ],
      studioName: "Use Case",
      projectTitle: "Computer Use",
    },
    {
      imageSrc: "/images/solutions/satellite-imagery/satellite_smoke_revised.jpeg",
      boxes: [
        {
          x_min: 0.23102908381791523,
          y_min: 0.11304750315784512,
          x_max: 0.3926268301605794,
          y_max: 0.45586745285388514,
          label: "smoke"
        }
      ],
      studioName: "Use Case",
      projectTitle: "Security & Safety",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
      {demoSections.map((section, index) => (
        <VideoSection
          key={index}
          imageSrc={section.imageSrc}
          boxes={section.boxes}
          studioName={section.studioName}
          projectTitle={section.projectTitle}
          className="aspect-video"
        />
      ))}
    </div>
  );
};

export { Demos };

